import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import {
  getEncode, patchEncode, getEncodeCaps, parseCapsForChannel,
  getChannelTitles, getSnapSchedule, patchSnapSchedule,
} from "../../api/config";
import type { Device } from "../../types";
import type { ChannelCaps, EncodeChannel, StreamFormat, VideoFormat } from "../../types/encode";

const SNAP_TYPES: { value: number; label: string }[] = [
  { value: 0, label: "Snapshot normal (programada)" },
  { value: 1, label: "Snapshot por movimiento" },
  { value: 2, label: "Snapshot por alarma" },
];

function flattenForSet(prefix: string, value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (p: string, v: unknown): void => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) { v.forEach((x, i) => walk(`${p}[${i}]`, x)); return; }
    if (typeof v === "object") { Object.entries(v as Record<string, unknown>).forEach(([k, vv]) => walk(p ? `${p}.${k}` : k, vv)); return; }
    if (typeof v === "boolean") { out[p] = v ? "true" : "false"; return; }
    out[p] = String(v);
  };
  walk(prefix, value);
  return out;
}

export function SnapshotTab({ deviceId, device }: { deviceId: number; device: Device }) {
  const [encodeFull, setEncodeFull] = useState<{ Encode?: EncodeChannel[] } | null>(null);
  const [snapFull, setSnapFull] = useState<{ Snap?: any[] } | null>(null);
  const [caps, setCaps] = useState<ChannelCaps | null>(null);
  const [channelIdx, setChannelIdx] = useState(0);
  const [snapTypeIdx, setSnapTypeIdx] = useState(0);
  const [snapForm, setSnapForm] = useState<StreamFormat | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channelNames, setChannelNames] = useState<string[]>([]);
  const [applyAllOpen, setApplyAllOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [enc, snap] = await Promise.all([
        getEncode(deviceId),
        getSnapSchedule(deviceId).catch(() => ({ Snap: [] })),
      ]);
      setEncodeFull(enc);
      setSnapFull(snap);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo snapshot");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    getChannelTitles(deviceId)
      .then((d) => setChannelNames(d.titles.map((t) => t.name)))
      .catch(() => setChannelNames([]));
  }, [deviceId]);

  useEffect(() => {
    getEncodeCaps(deviceId, channelIdx + 1)
      .then((r) => setCaps(parseCapsForChannel(r, 0)))
      .catch(() => setCaps(null));
  }, [deviceId, channelIdx]);

  useEffect(() => {
    const ch = encodeFull?.Encode?.[channelIdx];
    const slot = ch?.SnapFormat?.[snapTypeIdx];
    if (slot) setSnapForm(JSON.parse(JSON.stringify(slot)));
  }, [encodeFull, channelIdx, snapTypeIdx]);

  const totalChannels = encodeFull?.Encode?.length || device.channels_count || 1;
  const channelOptions = useMemo(
    () => Array.from({ length: totalChannels }, (_, i) => ({
      value: String(i),
      label: channelNames[i] ? `CH${i + 1} - ${channelNames[i]}` : `CH${i + 1}`,
    })),
    [totalChannels, channelNames],
  );

  if (loading || !encodeFull) return <Spinner />;
  if (!snapForm) return <div className="text-sm text-muted">El canal no tiene SnapFormat configurado.</div>;

  const video: VideoFormat = snapForm.Video || ({} as VideoFormat);
  const resolutionOpts = (caps?.main?.resolutionTypes.length ? caps.main.resolutionTypes : [video.CustomResolutionName || "1080P"]).map((r) => ({ value: r, label: r }));
  const qualityRange = video.QualityRange || 6;

  const updateVideo = (patch: Partial<VideoFormat>) => {
    setSnapForm((p) => p ? { ...p, Video: { ...p.Video, ...patch } } : p);
  };

  const buildPayload = (idx: number): Record<string, string> => {
    if (!snapForm) return {};
    const prefix = `Encode[${idx}].SnapFormat[${snapTypeIdx}]`;
    return flattenForSet(prefix, snapForm);
  };

  const save = async () => {
    setSaving(true);
    try {
      await patchEncode(deviceId, buildPayload(channelIdx));
      toast.success(`Snapshot CH${channelIdx + 1} actualizado`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al guardar");
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = async () => {
    setApplyAllOpen(false);
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (let i = 0; i < totalChannels; i++) {
        Object.assign(payload, buildPayload(i));
      }
      await patchEncode(deviceId, payload);
      toast.success(`Aplicado a ${totalChannels} canales`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo aplicando a todos");
    } finally {
      setSaving(false);
    }
  };

  const snapSchedule = snapFull?.Snap?.[channelIdx];
  const holidayEnable = !!snapSchedule?.HolidayEnable;
  const toggleHoliday = async () => {
    try {
      setSaving(true);
      await patchSnapSchedule(deviceId, { [`Snap[${channelIdx}].HolidayEnable`]: holidayEnable ? "false" : "true" });
      toast.success("Programación actualizada");
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Snapshot (foto)</h2>
          <p className="text-xs text-muted mt-0.5">
            Calidad y tamaño de las fotos JPG que el NVR captura (manual, por movimiento o por alarma).
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            label="Canal"
            options={channelOptions}
            value={String(channelIdx)}
            onChange={(e) => setChannelIdx(Number(e.target.value))}
            className="min-w-[200px]"
          />
          <Button variant="secondary" onClick={() => setApplyAllOpen(true)} disabled={saving}>
            Aplicar a todos los canales
          </Button>
        </div>
      </header>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Calidad de la foto</h3>
          <Select
            label="Tipo de snapshot"
            options={SNAP_TYPES.map((s) => ({ value: String(s.value), label: s.label }))}
            value={String(snapTypeIdx)}
            onChange={(e) => setSnapTypeIdx(Number(e.target.value))}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Select
            label="Tamaño / Resolución"
            options={resolutionOpts}
            value={String(video.CustomResolutionName || "")}
            onChange={(e) => updateVideo({ CustomResolutionName: e.target.value })}
          />
          <Input
            label={`Calidad (1-${qualityRange})`}
            type="number"
            min={1}
            max={qualityRange}
            value={String(video.Quality ?? 4)}
            onChange={(e) => updateVideo({ Quality: Number(e.target.value) })}
          />
          <Input
            label="FPS (1=1 foto/s)"
            type="number"
            min={1}
            max={25}
            value={String(video.FPS ?? 1)}
            onChange={(e) => updateVideo({ FPS: Number(e.target.value) })}
          />
        </div>
        <p className="text-xs text-muted">
          Formato fijo: MJPG (este NVR no soporta otros codecs para snapshot).
        </p>
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-3">
        <h3 className="text-sm font-semibold">Programación de capturas</h3>
        <p className="text-xs text-muted">
          La programación detallada por día/hora se administra desde el menú web del propio NVR. Aquí podés
          activar/desactivar el modo "feriados" del canal.
        </p>
        {snapSchedule ? (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={holidayEnable} onChange={toggleHoliday} />
            <span>Programación de feriados activada</span>
          </label>
        ) : (
          <div className="text-xs text-muted">Sin programación cargada para este canal.</div>
        )}
      </section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={save} loading={saving}>Guardar</Button>
        <Button variant="ghost" onClick={reload} disabled={saving}>Restablecer</Button>
      </div>

      <ConfirmDialog
        open={applyAllOpen}
        title="Aplicar a todos los canales"
        message={`Esta accion va a sobrescribir la calidad de snapshot (${SNAP_TYPES[snapTypeIdx].label}) en los ${totalChannels} canales. ¿Continuar?`}
        confirmText="Aplicar a todos"
        destructive
        onConfirm={applyToAll}
        onCancel={() => setApplyAllOpen(false)}
      />
    </div>
  );
}
